import { apiErrorMessage } from '@/utils/apiError';
import { createLatestGuard } from '@/utils/latestRequest';
import { IST_OFFSET_MS, istClock } from '@/utils/cricketCalendar';
import type {
  PvpLiveBots,
  PvpLiveCheck,
  PvpLiveCheckKey,
  PvpLiveDto,
  PvpLiveMatchCounts,
  PvpLiveQuery,
  PvpLiveRange,
  PvpLiveReason,
  PvpLiveStatus,
} from '@/types/pvp';

// The answer may arrive with null keys dropped (NullRemovalInterceptor), so
// every nullable field is read with `== null`: null and missing alike.

// ---- Range, bots and timing ----

export const LIVE_RANGES: Array<{ label: string; value: PvpLiveRange }> = [
  { label: '1 h', value: '1h' },
  { label: '24 h', value: '24h' },
  { label: '7 d', value: '7d' },
];

export const DEFAULT_RANGE: PvpLiveRange = '24h';
export const DEFAULT_BOTS: PvpLiveBots = 'include';

/** How often the open page reads again (the server caches each answer 10 s). */
export const REFRESH_MS = 15_000;

/** Newest health minute older than this (before generatedAt) = the stale banner. */
export const STALE_MS = 180_000;

export const liveQuery = (range: PvpLiveRange, includeBots: boolean): PvpLiveQuery => ({
  range,
  bots: includeBots ? 'include' : 'exclude',
});

// ---- Numbers ----

/** A number with thousands separators; "—" for null, missing or not finite, never 0. */
export function fmtNum(value: number | null | undefined, maxDigits = 2): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-IN', { maximumFractionDigits: maxDigits });
}

/** A completion percentage with one decimal: "97.5%"; "—" when there were no matches. */
export function fmtPct(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString('en-IN', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

/** A check's value with its unit ("0.4 per 1,000 balls", "0.5%", "18 ms"); a count is bare. */
export function fmtCheckValue(check: Pick<PvpLiveCheck, 'value' | 'unit'>): string {
  if (check.value == null) return '—';
  const n = fmtNum(check.value);
  if (n === '—') return n;
  const unit = (check.unit ?? '').trim();
  if (unit === '' || unit === 'count') return n;
  if (unit.startsWith('%')) return `${n}${unit}`;
  return `${n} ${unit}`;
}

/** "warn at 1, bad at 5", or "warn below 99.5, bad below 98" where higher is better. */
export function limitsLine(check: Pick<PvpLiveCheck, 'warnAt' | 'badAt' | 'higherIsBetter'>) {
  const word = check.higherIsBetter ? 'below' : 'at';
  return `warn ${word} ${fmtNum(check.warnAt)}, bad ${word} ${fmtNum(check.badAt)}`;
}

// ---- Times (IST) ----

const MINUTE_MS = 60_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'HH:mm' IST of an ISO instant. */
export const istTime = (iso: string): string => istClock(iso);

/**
 * The axis label of a bucket: 'HH:mm' IST for 1 h and 24 h, 'DD Mon HH:mm' IST
 * for 7 d. Hourly buckets start on UTC hours, which are :30 in IST, so the
 * real start minute is shown rather than ':00'.
 */
export function bucketLabel(iso: string, range: PvpLiveRange): string {
  if (range !== '7d') return istClock(iso);
  const shifted = new Date(Date.parse(iso) + IST_OFFSET_MS);
  const day = String(shifted.getUTCDate()).padStart(2, '0');
  return `${day} ${MONTHS[shifted.getUTCMonth()]} ${istClock(iso)}`;
}

/** An age in words: "10 min" below 2 hours, then "3 h" below 2 days, then "2 days". */
export function fmtAge(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / MINUTE_MS));
  if (minutes < 120) return `${fmtNum(minutes, 0)} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${fmtNum(hours, 0)} h`;
  return `${fmtNum(Math.floor(hours / 24), 0)} days`;
}

/**
 * The line above the numbers: "Last 24 h in 10-min buckets · bot matches
 * included · newest health 14:29 IST · answered 14:30 IST". It names the
 * answer's own range, so data kept from before a range change says so.
 */
export function periodLine(dto: PvpLiveDto): string {
  const range = LIVE_RANGES.find((r) => r.value === dto.range)?.label ?? dto.range;
  const bots = dto.bots === 'exclude' ? 'bot matches left out' : 'bot matches included';
  const health = dto.asOf == null ? 'no health rows' : `newest health ${istTime(dto.asOf)} IST`;
  return [
    `Last ${range} in ${fmtNum(dto.bucketMin, 0)}-min buckets`,
    bots,
    health,
    `answered ${istTime(dto.generatedAt)} IST`,
  ].join(' · ');
}

// ---- Freshness ----

const isZero = (value: number | null | undefined) => !value;

/**
 * No PvP server has ever reported (no health minute in the 14-day window) and
 * every number is 0 or missing: the page shows its empty state.
 */
export function isEmpty(dto: PvpLiveDto): boolean {
  if (dto.asOf != null) return false;
  const now = dto.now ?? ({} as Partial<PvpLiveDto['now']>);
  const nowZero = [
    now.ccu,
    now.ccuPeakToday?.value,
    now.liveMatches,
    now.queue,
    now.processesUp,
    now.processesLost,
  ].every(isZero);
  const matchesZero = isZero(dto.matches?.lastHour?.done) && isZero(dto.matches?.today?.done);
  const onlineZero = (dto.series?.online ?? []).every(
    (p) => isZero(p.ccu) && isZero(p.liveMatches) && isZero(p.queue)
  );
  const doneZero = (dto.series?.matches ?? []).every((p) => isZero(p.done));
  const checksIdle = (dto.checks ?? []).every((c) => c.value == null || c.value === 0);
  const noProcesses = (dto.processes ?? []).length === 0;
  return nowZero && matchesZero && onlineZero && doneZero && checksIdle && noProcesses;
}

/** generatedAt (or `now`) minus asOf in ms; null when no process wrote health. */
export function healthAgeMs(dto: PvpLiveDto, now?: number): number | null {
  if (dto.asOf == null) return null;
  const ref = now ?? Date.parse(dto.generatedAt);
  return ref - Date.parse(dto.asOf);
}

/**
 * The stale banner shows when the newest health minute is more than 3 minutes
 * older than generatedAt (the server's clock, so a skewed browser clock cannot
 * raise it), or when no process wrote health but the answer still has numbers.
 * An empty answer shows the empty state instead.
 */
export function isStale(dto: PvpLiveDto, now?: number): boolean {
  if (isEmpty(dto)) return false;
  const age = healthAgeMs(dto, now);
  return age == null || age > STALE_MS;
}

export function staleMessage(dto: PvpLiveDto, now?: number): string {
  const age = healthAgeMs(dto, now);
  const since = age == null ? 'in the last 14 days' : `for ${fmtAge(age)}`;
  return `No PvP server has written health ${since}: the servers may be down, or the replica is behind.`;
}

// ---- Tiles ----

export type LiveTileKey = 'online' | 'liveMatches' | 'done' | 'completion' | 'servers';

/** The checks whose status colours each tile's border. */
export const TILE_CHECKS: Record<LiveTileKey, PvpLiveCheckKey[]> = {
  online: [],
  liveMatches: ['turnedAway'],
  done: ['serverEnded'],
  completion: ['serverEnded', 'cuts'],
  servers: ['processes', 'checker', 'lag'],
};

const STATUS_RANK: Record<PvpLiveStatus, number> = { idle: 0, good: 1, warn: 2, bad: 3 };

/** The worst of some statuses (bad, warn, good, idle in that order); idle for none. */
export function worstStatus(statuses: PvpLiveStatus[]): PvpLiveStatus {
  return statuses.reduce<PvpLiveStatus>(
    (worst, s) => ((STATUS_RANK[s] ?? 0) > STATUS_RANK[worst] ? s : worst),
    'idle'
  );
}

/** Each tile's tone: the worst status of its mapped checks. */
export function tileTone(dto: PvpLiveDto): Record<LiveTileKey, PvpLiveStatus> {
  const byKey = new Map((dto.checks ?? []).map((c) => [c.key, c.status]));
  const tone = (keys: PvpLiveCheckKey[]) =>
    worstStatus(keys.map((k) => byKey.get(k)).filter((s): s is PvpLiveStatus => !!s));
  return {
    online: tone(TILE_CHECKS.online),
    liveMatches: tone(TILE_CHECKS.liveMatches),
    done: tone(TILE_CHECKS.done),
    completion: tone(TILE_CHECKS.completion),
    servers: tone(TILE_CHECKS.servers),
  };
}

/** The border colour of a tile: the warning or error colour, none for good or idle. */
export function toneColor(
  status: PvpLiveStatus,
  colors: { warning: string; error: string }
): string | undefined {
  if (status === 'bad') return colors.error;
  if (status === 'warn') return colors.warning;
  return undefined;
}

type LiveNow = PvpLiveDto['now'];

/** "peak today 2,345 at 13:10 IST" (IST day). */
export function peakLine(now: LiveNow): string {
  const peak = now.ccuPeakToday;
  const value = `peak today ${fmtNum(peak?.value ?? 0, 0)}`;
  return peak?.at == null ? value : `${value} at ${istTime(peak.at)} IST`;
}

/** "41% of capacity · waiting 7", or "no limit set · waiting 7". */
export function capacityLine(now: LiveNow): string {
  const cap = now.capacity;
  const share =
    cap == null || !(cap > 0)
      ? 'no limit set'
      : `${fmtNum(Math.round((100 * now.liveMatches) / cap), 0)}% of capacity`;
  return `${share} · waiting ${fmtNum(now.queue, 0)}`;
}

/** The reasons a match did not complete, in the words the page shows. */
const LOST_REASONS: Array<[PvpLiveReason, string]> = [
  ['forfeit', 'forfeit'],
  ['quit', 'quit'],
  ['disconnect', 'disconnect'],
  ['abort', 'aborted'],
];

/** The top two reasons matches did not complete: "quit 2 · forfeit 1". */
export function reasonsLine(counts: PvpLiveMatchCounts): string {
  if (!(counts.done > 0)) return 'no matches';
  const top = LOST_REASONS.map(([key, word]) => ({ word, n: counts.byReason?.[key] ?? 0 }))
    .filter((r) => r.n > 0)
    .sort((a, b) => b.n - a.n)
    .slice(0, 2);
  if (top.length === 0) return 'all completed';
  return top.map((r) => `${r.word} ${fmtNum(r.n, 0)}`).join(' · ');
}

/** "2 / 3": processes up out of up plus lost. */
export const serversValue = (now: LiveNow): string =>
  `${fmtNum(now.processesUp, 0)} / ${fmtNum(now.processesUp + now.processesLost, 0)}`;

export function serversLine(now: LiveNow): string {
  if (now.processesLost > 0) return `${fmtNum(now.processesLost, 0)} lost`;
  if (now.processesUp > 0) return 'none lost';
  return 'none seen in 15 min';
}

/** pvp-sim reachable: Yes / No, or a dash when the process has no sim. */
export const simLabel = (simUp: boolean | null | undefined): string =>
  simUp == null ? '—' : simUp ? 'Yes' : 'No';

// ---- Checks ----

export const STATUS_BADGE: Record<PvpLiveStatus, 'success' | 'warning' | 'error' | 'default'> = {
  good: 'success',
  warn: 'warning',
  bad: 'error',
  idle: 'default',
};

/** The status in words, beside the coloured dot. */
export const STATUS_WORD: Record<PvpLiveStatus, string> = {
  good: 'good',
  warn: 'warning',
  bad: 'bad',
  idle: 'idle',
};

/** One plain sentence per check: what its number means. */
export const CHECK_MEANING: Record<PvpLiveCheckKey, string> = {
  sync: "Balls where a phone's result differed from the server's check with the same inputs, per 1,000 balls checked: each one is a ball the two players may have seen differently.",
  checkFailed:
    'Share of ball checks that got no usable answer from pvp-sim (down, an error or a bad answer); the server asks again, but a rising share means the checker is struggling.',
  serverEnded:
    'Share of finished matches the server aborted instead of playing to a result, for example when no ball check ever succeeded.',
  turnedAway:
    'Share of match requests turned away as busy: no room on the server, pvp-sim not ready, or pvp-sim refusing the batters at match setup.',
  cuts: 'Players cut after their phone went silent for 8 seconds, per 100 finished matches.',
  refunds:
    'Matches whose entry fees were refunded, at the end or by the reconcile sweep, per 100 finished matches.',
  processes:
    'PvP processes lost now, plus those declared dead after their heartbeat lapsed or that found themselves declared dead; a clean stop does not count.',
  checker:
    "PvP processes up whose pvp-sim (the server's ball checker) does not answer, so their matches cannot be checked.",
  checkTime:
    'The slowest pvp-sim call in the window; slow calls hold up ball results for both phones.',
  lag: 'The worst event-loop lag (p99) of any PvP process; high lag delays every message that process handles.',
  replays:
    'Share of settled matches whose replay was saved to the archive; the rest failed to save (higher is better).',
};

// ---- Chart rows ----

export type OnlineSeries = 'Online' | 'Live matches' | 'Waiting';
export const ONLINE_SERIES: OnlineSeries[] = ['Online', 'Live matches', 'Waiting'];

export interface OnlineRow {
  t: string;
  series: OnlineSeries;
  value: number;
}

/** Three lines from series.online (peak per bucket), in bucket order. */
export function onlineRows(dto: PvpLiveDto): OnlineRow[] {
  return (dto.series?.online ?? []).flatMap((p) => [
    { t: p.t, series: 'Online' as const, value: p.ccu ?? 0 },
    { t: p.t, series: 'Live matches' as const, value: p.liveMatches ?? 0 },
    { t: p.t, series: 'Waiting' as const, value: p.queue ?? 0 },
  ]);
}

export const doneRows = (dto: PvpLiveDto): Array<{ t: string; done: number }> =>
  (dto.series?.matches ?? []).map((p) => ({ t: p.t, done: p.done ?? 0 }));

/** Completion % per bucket; null (no match ended) stays null so the line breaks there. */
export const completionRows = (dto: PvpLiveDto): Array<{ t: string; value: number | null }> =>
  (dto.series?.matches ?? []).map((p) => ({ t: p.t, value: p.completionPct ?? null }));

export const checkRows = (check: PvpLiveCheck): Array<{ t: string; value: number | null }> =>
  (check.series ?? []).map((p) => ({ t: p.t, value: p.value ?? null }));

/** Chart axes of one range: x = bucket start labelled in IST, y through `yFormat`. */
export const timeAxis = (
  range: PvpLiveRange,
  yFormat: (v: number) => string = (v) => fmtNum(v)
) => ({
  x: { labelAutoHide: true, labelFormatter: (v: string) => bucketLabel(v, range) },
  y: { labelFormatter: (v: number) => yFormat(Number(v)) },
});

/** A chart tooltip's title: the bucket start in IST. */
export const tooltipTitle =
  (range: PvpLiveRange) =>
  (d: { t: string }): string =>
    `${bucketLabel(d.t, range)} IST`;

// ---- Loading ----

export interface LiveState {
  data: PvpLiveDto | null;
  loading: boolean;
  error: string | null;
}

/** Why a load runs: the first load and Refresh spin; background polls stay quiet. */
export type LoadReason = 'load' | 'manual' | 'poll';

type FetchLive = (query: PvpLiveQuery) => Promise<{ data: PvpLiveDto }>;

/**
 * Loads the live answer and reports each step to `apply`. Only the newest call
 * applies its answer, so a slow older read cannot overwrite a newer range. A
 * failure keeps the last good data on screen and only sets the error.
 */
export function createLiveLoader(fetchLive: FetchLive, apply: (patch: Partial<LiveState>) => void) {
  const guard = createLatestGuard();
  return {
    async load(query: PvpLiveQuery, reason: LoadReason = 'manual'): Promise<void> {
      const isLatest = guard.begin();
      if (reason !== 'poll') apply({ loading: true });
      try {
        const response = await fetchLive(query);
        if (!isLatest()) return;
        apply({ loading: false, data: response.data, error: null });
      } catch (e) {
        if (!isLatest()) return;
        apply({ loading: false, error: apiErrorMessage(e, 'Failed to load the PvP live view') });
      }
    },
  };
}
